/**
 * 集計表の色付け。
 *
 * **判定の基準(しきい値)** は現行スプレッドシート「toそうしさん日報」の条件付き書式から抽出したもの(2026-09-09)で、変えない:
 *  - 時間別取得件数: 3件以上 ／ 1通話あたりの取得率: 5%以上 ／ 1取得あたりの通話時間: 15分以下 ／ 1取得あたりの稼働時間: 24分以下
 *  - RANK: 見込/h(不用品は10倍)で S 5.0以上 / A 3.5〜 / B+ 2.5〜 / B- 2.0〜 / C 2.0未満(calc.ts の rankOf)
 *  - 原価率: 月次設定の目標原価率
 *
 * **色の出し方** は UIデザインシステム(docs/design-system.md。2026-09-18 導入 D-89、集計画面へ展開 D-90)に合わせる:
 *  - 状態色は意味で使う: 良い=緑の文字(太字) ／ 目標超過=赤の文字＋印 ／ 0 は薄い灰色。セル全体の塗りつぶしはしない
 *  - RANK は評価段階の色(S 紫・A 青・B+ 青緑・B- スレート・C 灰)。見込/h の数字も同じ色(修正1)
 *  - 原価率の「良好」は基準が業務設定として決まるまで出さない(P-50)。目標以内は色なし
 *
 * 各指標の説明(HELP)は見出しの吹き出し(HelpTip)に出す(⑦指示)。
 */
import type { Help, Verdict } from '@core/components/HelpTip';
import { formatPercent, formatPointDiff } from '@core/utils/formatters';

export const THRESHOLDS = {
  hourlyHot: 3, // 時間別取得件数 ≥3 → 良い
  hitRate: 5, // 取得率 ≥5% → 良い
  talkPerApptSec: 15 * 60, // 1取得あたり通話時間 ≤15分 → 良い
  workPerApptSec: 24 * 60, // 1取得あたり稼働時間 ≤24分 → 良い
} as const;

/** 吹き出しの色見本(ui.css の .tip-sw.sw-*) */
const SW = { none: 'sw-none', good: 'sw-good', over: 'sw-over', muted: 'sw-muted' } as const;

/** 項目名にカーソルを合わせたときの説明(指標の意味・判定の向き・色の意味) */
export const HELP = {
  rank: {
    title: 'RANK',
    what: '1時間あたりの見込取得数(取得数 ÷ 稼働合計(受付＋通話＋後処理))から判定。不用品は10倍した値で判定。見込/h の数字も同じ色',
    better: 'S が最も良く、C が最も悪い',
    colors: [
      { cls: 'sw-grade-1', range: 'S: 5.0以上', meaning: 'とても良い' },
      { cls: 'sw-grade-2', range: 'A: 3.5〜5.0', meaning: '良い' },
      { cls: 'sw-grade-3', range: 'B+: 2.5〜3.5', meaning: 'やや良い' },
      { cls: 'sw-grade-4', range: 'B-: 2.0〜2.5', meaning: '普通' },
      { cls: 'sw-grade-5', range: 'C: 2.0未満', meaning: '悪い' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  hourly: {
    title: '時間別取得件数',
    what: 'その時台(9時台〜19時台)に取得した件数',
    better: '多いほど良い',
    colors: [
      { cls: SW.good, range: '3件以上', meaning: '良い(緑の太字)' },
      { cls: SW.none, range: '1〜2件', meaning: '普通' },
      { cls: SW.muted, range: '0件', meaning: '薄い灰色' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  apiCalls: {
    title: '通話回数',
    what: '完了した通話の回数。**親の行はオペレーターレポート、会社別の行は BlueBean API の記録**から数えています',
    better: '参考値(色は付けない)',
    colors: [],
    source: 'どちらも「完了した通話」を数えた同じ意味の数字です。オペレーターレポートは人ごとの合計しか無いため、会社別はAPIの記録から出しています。APIの記録が無い日は「—」',
  },
  hitRate: {
    title: '取得率',
    what: '取得数 ÷ 通話回数(1通話あたりの取得率)',
    better: '高いほど良い',
    colors: [
      { cls: SW.good, range: '5%以上', meaning: '良い(緑の太字)' },
      { cls: SW.none, range: '5%未満', meaning: '普通' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  talkPerAppt: {
    title: '1取得あたりの通話時間',
    what: '通話時間 ÷ 取得数(1件取るのにかかった通話時間)',
    better: '短いほど良い',
    colors: [
      { cls: SW.good, range: '15分以下', meaning: '良い(緑の太字)' },
      { cls: SW.none, range: '15分超', meaning: '普通' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  workPerAppt: {
    title: '1取得あたりの稼働時間',
    what: '(受付＋通話＋後処理) ÷ 取得数(1件取るのにかかった稼働時間)',
    better: '短いほど良い',
    colors: [
      { cls: SW.good, range: '24分以下', meaning: '良い(緑の太字)' },
      { cls: SW.none, range: '24分超', meaning: '普通' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  apph: {
    title: '1時間あたりの見込取得数(見込/h)',
    what: '取得数 ÷ 稼働合計(受付＋通話＋後処理)。RANK の判定に使い、数字は RANK と同じ色',
    better: '高いほど良い',
    colors: [
      { cls: 'sw-grade-1', range: 'S', meaning: '5.0以上(不用品は0.5以上)' },
      { cls: 'sw-grade-2', range: 'A', meaning: '3.5〜5.0' },
      { cls: 'sw-grade-3', range: 'B+', meaning: '2.5〜3.5' },
      { cls: 'sw-grade-4', range: 'B-', meaning: '2.0〜2.5' },
      { cls: 'sw-grade-5', range: 'C', meaning: '2.0未満' },
    ],
    source: '判定の基準は現行シート「日次【集計】」',
  },
  costRate: {
    title: '原価率',
    what: '報酬(原価) ÷ 売上。売上は納品ベースの実績があれば実績、無ければ取得ベースの見込み',
    better: '低いほど良い(目標は月次設定の目標原価率)',
    colors: [
      { cls: SW.over, range: '目標を超える', meaning: '超過(赤字と印)' },
      { cls: SW.none, range: '目標以内', meaning: '色なし' },
    ],
    source: '目標は「PL」の月次設定',
  },
  grossRate: {
    title: '粗利率',
    what: '粗利(売上 − 報酬) ÷ 売上',
    better: '高いほど良い',
  },
  revenue: {
    title: '売上見込み',
    what: '取得件数 × 売上単価(割振会社の単価)。納品前のため見込み',
    better: '高いほど良い',
  },
  actualSales: {
    title: '売上実績',
    what: '納品件数 × 売上単価。取得したアポは翌日に各会社へ納品し、納品前のキャンセル分は減る',
    better: '高いほど良い',
  },
  cancelRate: {
    title: 'キャンセル率',
    what: 'キャンセル合計(在宅＋出勤) ÷ 実績アポ数',
    better: '低いほど良い',
  },
  wage: {
    title: '報酬',
    what: '時給(出勤・在宅時給・タイミー)または件数単価(在宅歩合)。オペレーターマスタのその日の値。管理者は表示しない',
    better: '—',
  },
  workSec: {
    title: '稼働合計',
    what: '受付＋通話＋後処理(休憩・離席は含まない)。実稼働(h)はこれを時間にしたもの',
    better: '—',
  },
  pay: {
    title: '報酬額見込',
    what: '時給×実稼働(出勤は交通費込み)、件数単価×取得数(在宅歩合)、時給×稼働合計(在宅時給)の参考値。確定の人件費は「日報/サマリ」で持つ',
    better: '低いほど原価率は良い',
  },
} satisfies Record<string, Help>;

// ---- セルの状態(色は ui.css の .cell-*。判定の基準は上の THRESHOLDS と目標) -------------------

/** good=良い(緑の太字) / over=目標超過(赤字＋印) / muted=0 など薄く / ''=色なし */
export type CellTone = '' | 'good' | 'over' | 'muted';

export function hourlyTone(n: number): CellTone {
  if (n >= THRESHOLDS.hourlyHot) return 'good';
  if (n === 0) return 'muted';
  return '';
}

export function hitRateTone(pct: number | null): CellTone {
  return pct !== null && pct >= THRESHOLDS.hitRate ? 'good' : '';
}

export function talkPerApptTone(sec: number | null): CellTone {
  return sec !== null && sec <= THRESHOLDS.talkPerApptSec ? 'good' : '';
}

export function workPerApptTone(sec: number | null): CellTone {
  return sec !== null && sec <= THRESHOLDS.workPerApptSec ? 'good' : '';
}

/** 原価率(%): 目標を超えたら over。目標以内は色なし(「良好」の基準は未設定。P-50) */
export function costRateTone(pct: number | null, target: number | null | undefined): CellTone {
  if (pct === null || !Number.isFinite(pct) || target === null || target === undefined) return '';
  return pct > target ? 'over' : '';
}

// ---- 今の値の判定(吹き出しの先頭に出す) ------------------------------------------

export function costRateVerdict(pct: number | null, target: number | null = 45): Verdict | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  const v = formatPercent(pct);
  if (target === null) return { level: 'normal', reason: v };
  const diff = pct - target;
  // 「目標以上」で赤(2026-09-24 発注者指示「給湯器45%以上・不用品50%以上で赤」)
  return diff >= 0
    ? { level: 'bad', reason: `${v}: 目標 ${formatPercent(target)} 以上(${formatPointDiff(diff)})` }
    : { level: 'normal', reason: `${v}: 目標 ${formatPercent(target)} 未満(${formatPointDiff(diff)})` };
}

export function grossRateVerdict(pct: number | null): Verdict | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  if (pct >= 55.5) return { level: 'good', reason: `${formatPercent(pct)}: 原価率が目標(44.5%)以内` };
  if (pct >= 40) return { level: 'normal', reason: `${formatPercent(pct)}: 原価率が目標をやや超えている` };
  return { level: 'bad', reason: `${formatPercent(pct)}: 原価率が高い` };
}
